import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ArrowUpRight, ArrowDownRight, ArrowRight, Calendar, CheckCheck, Inbox } from "lucide-react";
import { toast } from "sonner";

const TYPE_ICON = {
  band_upgrade: <ArrowUpRight className="h-4 w-4 text-emerald-700" />,
  band_downgrade: <ArrowDownRight className="h-4 w-4 text-amber-700" />,
  new_certificate: <ArrowRight className="h-4 w-4 text-slate-700" />,
  expiring_soon: <Calendar className="h-4 w-4 text-amber-700" />,
};

const SEVERITY_STYLE = {
  success: "border-l-emerald-600 bg-emerald-50/40",
  warning: "border-l-amber-500 bg-amber-50/40",
  info: "border-l-slate-400 bg-slate-50/40",
};

export default function AlertsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/alerts", { params: { limit: 200, unread_only: filter === "unread" } });
      setItems(data.items);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    await api.post(`/alerts/${id}/read`);
    setItems(prev => prev.map(a => a.id === id ? { ...a, read: true } : a));
  };

  const markAllRead = async () => {
    const { data } = await api.post("/alerts/read-all");
    toast.success(`Marked ${data.updated} alerts as read`);
    load();
  };

  return (
    <div className="p-8 space-y-6 fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Notifications</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mt-1">Alerts</h1>
          <p className="text-sm text-muted-foreground mt-1">Every EPC change detected from the gov register lands here.</p>
        </div>
        <Button onClick={markAllRead} variant="outline" className="rounded-none border-slate-300" data-testid="mark-all-read">
          <CheckCheck className="h-4 w-4 mr-2" /> Mark all read
        </Button>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="rounded-none bg-slate-100 p-1 h-auto">
          <TabsTrigger value="all" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none" data-testid="filter-all">All</TabsTrigger>
          <TabsTrigger value="unread" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none" data-testid="filter-unread">Unread</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="rounded-none border-border divide-y divide-border">
        {loading ? (
          [...Array(5)].map((_, i) => <div key={i} className="p-5"><Skeleton className="h-12" /></div>)
        ) : items.length === 0 ? (
          <div className="p-16 text-center">
            <Inbox className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <div className="text-lg font-medium">No alerts</div>
            <div className="text-sm text-muted-foreground mt-1">Run "Check All" to start detecting changes.</div>
          </div>
        ) : items.map((a) => (
          <div
            key={a.id}
            className={`p-5 border-l-2 ${SEVERITY_STYLE[a.severity] || SEVERITY_STYLE.info} ${!a.read ? "" : "opacity-70"}`}
            data-testid={`alert-row-${a.id}`}
          >
            <div className="flex items-start gap-4">
              <div className="mt-0.5">{TYPE_ICON[a.type]}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-xs uppercase tracking-wider font-semibold">{a.type.replace("_", " ")}</div>
                  {!a.read && <span className="h-1.5 w-1.5 bg-slate-900 rounded-full" />}
                </div>
                <div className="font-medium mt-1">{a.address}</div>
                <div className="text-sm text-muted-foreground mt-0.5">{a.message}</div>
                <div className="text-xs text-muted-foreground font-mono mt-2">
                  {new Date(a.created_at).toLocaleString("en-GB")} · UPRN {a.uprn}
                </div>
              </div>
              {!a.read && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-none h-7 text-xs"
                  onClick={() => markRead(a.id)}
                  data-testid={`mark-read-${a.id}`}
                >
                  Mark read
                </Button>
              )}
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}
